import React, { useState } from 'react';
import WarningAmber from '@mui/icons-material/WarningAmber';
import ErrorOutline from '@mui/icons-material/ErrorOutline';
import { useHistory, useLocation } from 'react-router-dom';
import { erpSettingsPathFromPathname } from 'pages/Layout/erpModulePaths';
import { useAppSelector } from 'redux/hooks';
import { selectPermission } from 'redux/global';
import { PERM } from 'pages/Settings/redux';
import { usePath } from 'pages/hooks';
import { useStaffSeats } from 'pages/hooks/useStaffSeats';
import { formatSeatsCapacityLabel } from 'pages/Settings/seatsPlanCapacity';
import LayoutAlert from './LayoutAlert';
import LayoutAlertPresence from './LayoutAlertPresence';

const DISMISS_STORAGE_KEY = 'user_seats_alert_dismissed_v1';
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;
const warnAccent = '#a67c00';
const criticalAccent = '#c62828';

type DismissState = { warnUntil: number; criticalUntil: number };

const emptyDismissState = (): DismissState => ({ warnUntil: 0, criticalUntil: 0 });

const readDismissState = (): DismissState => {
  try {
    const raw = localStorage.getItem(DISMISS_STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      return {
        warnUntil: Number(parsed?.warnUntil) || 0,
        criticalUntil: Number(parsed?.criticalUntil) || 0,
      };
    }
  } catch {
    /* ignore */
  }
  return emptyDismissState();
};

const UserSeatsAlert = () => {
  const history = useHistory();
  const { pathname } = useLocation();
  const permission = useAppSelector(selectPermission);
  const { isHomePath } = usePath();
  const [dismissState, setDismissState] = useState(readDismissState);
  const { used, maxStaff, remaining, tone } = useStaffSeats();

  const canSee = (permission || 0) >= PERM.SUB_MANAGER;
  const dismissedUntil = tone === 'critical'
    ? dismissState.criticalUntil
    : dismissState.warnUntil;
  const isDismissed = !!tone && Date.now() < dismissedUntil;

  const open = !isHomePath && canSee && !!tone && !isDismissed;

  const isCritical = tone === 'critical';
  const accent = isCritical ? criticalAccent : warnAccent;

  const onGotIt = () => {
    const until = Date.now() + DISMISS_MS;
    const next: DismissState = {
      ...dismissState,
      ...(tone === 'critical' ? { criticalUntil: until } : { warnUntil: until }),
    };
    try {
      localStorage.setItem(DISMISS_STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* ignore */
    }
    setDismissState(next);
  };

  return (
    <LayoutAlertPresence open={open}>
      {tone ? (
        <LayoutAlert
          accent={accent}
          icon={isCritical
            ? <ErrorOutline fontSize="small" />
            : <WarningAmber fontSize="small" />}
          title={isCritical ? 'User seats full' : 'User seats running low'}
          text={isCritical
            ? `Your property is using all ${formatSeatsCapacityLabel(maxStaff)} user seats. Add more seats or remove a user before inviting employees or CEOs.`
            : `Your property has ${remaining.toLocaleString()} user seat${remaining === 1 ? '' : 's'} left (${used.toLocaleString()} / ${formatSeatsCapacityLabel(maxStaff)} in use).`}
          primaryVariant="outlined"
          secondaryAction={{ label: 'Got it', onClick: onGotIt }}
          primaryAction={{
            label: 'Upgrade seats',
            onClick: () => history.push(
              erpSettingsPathFromPathname(pathname, 'subscriptions', 'user-seats'),
            ),
          }}
        />
      ) : null}
    </LayoutAlertPresence>
  );
};

export default UserSeatsAlert;
